import {
    CheckCircle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    FileText,
    GraduationCap,
    Handshake,
    Headphones,
    Info,
    Megaphone,
    ShoppingCart,
    Target,
    Users,
    X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(true);
  const [showCompletedSections, setShowCompletedSections] = useState(false);
  const [activeGuide, setActiveGuide] = useState('marketing');
  const [showSetupSidebar, setShowSetupSidebar] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceModalPage, setServiceModalPage] = useState(1);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Progress is set to 100% to match the screenshot
  const progressPercentage = 100;

  const userGuides = [
    {
      id: 'marketing',
      title: 'Marketing Guide',
      toolsName: 'Marketing',
      icon: Megaphone,
      iconColor: 'text-orange-600'
    },
    {
      id: 'sales',
      title: 'Sales Guide', 
      toolsName: 'Sales',
      icon: Handshake,
      iconColor: 'text-blue-600'
    },
    {
      id: 'customer-service',
      title: 'Customer Service Guide',
      toolsName: 'Customer Service',
      icon: Headphones,
      iconColor: 'text-green-600'
    },
    {
      id: 'content',
      title: 'Content Guide',
      toolsName: 'Content',
      icon: FileText,
      iconColor: 'text-purple-600'
    },
    {
      id: 'commerce',
      title: 'Commerce Guide',
      toolsName: 'Commerce',
      icon: ShoppingCart,
      iconColor: 'text-pink-600'
    }
  ];

  const currentGuide = userGuides.find(guide => guide.id === activeGuide) || userGuides[0];

  const planActions = [
    {
      id: 'invite-team',
      title: 'Invite your team',
      icon: Users,
      trailingIcon: Info,
      completed: false
    }
  ];

  // Dynamic learning links based on active guide
  const getLearningLinks = (guideId: string) => {
    switch (guideId) {
      case 'sales':
        return [
          {
            id: 'sales-training',
            title: 'Get started with a 4 minute lesson on sales fundamentals',
            icon: GraduationCap,
            action: 'service-modal'
          },
          {
            id: 'support-customers',
            title: 'Support your customers with a shared inbox and tickets',
            icon: Headphones,
            external: true,
            url: 'https://knowledge.hubspot.com/get-started/support-your-customers'
          }
        ];
      case 'customer-service':
        return [
          {
            id: 'service-training',
            title: 'Get started with a 4 minute lesson on customer service',
            icon: GraduationCap,
            action: 'service-modal'
          },
          {
            id: 'support-customers',
            title: 'Support your customers with a shared inbox and tickets',
            icon: Headphones,
            external: true,
            url: 'https://knowledge.hubspot.com/get-started/support-your-customers'
          }
        ];
      case 'content':
        return [
          {
            id: 'content-training',
            title: 'Get started with a 4 minute lesson on content creation',
            icon: GraduationCap,
            action: 'service-modal'
          },
          {
            id: 'support-customers',
            title: 'Support your customers with a shared inbox and tickets',
            icon: Headphones,
            external: true,
            url: 'https://knowledge.hubspot.com/get-started/support-your-customers'
          }
        ];
      case 'commerce':
        return [
          {
            id: 'commerce-training',
            title: 'Get started with a 4 minute lesson on e-commerce',
            icon: GraduationCap,
            action: 'service-modal'
          },
          {
            id: 'support-customers',
            title: 'Support your customers with a shared inbox and tickets',
            icon: Headphones,
            external: true,
            url: 'https://knowledge.hubspot.com/get-started/support-your-customers'
          }
        ];
      default: // marketing
        return [
          {
            id: 'attract-audience',
            title: 'Get started with a 4 minute lesson on attracting the right audience',
            icon: GraduationCap,
            action: 'service-modal'
          },
          {
            id: 'support-customers',
            title: 'Support your customers with a shared inbox and tickets',
            icon: Headphones,
            external: true,
            url: 'https://knowledge.hubspot.com/get-started/support-your-customers'
          }
        ];
    }
  };

  const learningLinks = getLearningLinks(activeGuide);

  // Dynamic completed sections based on active guide
  const getCompletedSections = (guideId: string) => {
    switch (guideId) {
      case 'marketing':
        return [
          {
            id: 'marketing-basics',
            title: 'Recommended: All you need to get started',
            description: 'Great job! Continue to attract new customers with marketing tools.',
            nextButton: 'Next: Marketing tools',
            icon: Target
          },
          {
            id: 'marketing-hub',
            title: 'Attract new customers with Marketing Hub',
            description: 'Great job! We recommend you continue your set up with Sales tools.',
            nextButton: 'Next: Sales tools',
            icon: Megaphone
          }
        ];
      case 'sales':
        return [
          {
            id: 'sales-basics',
            title: 'Recommended: Sales fundamentals complete',
            description: 'Excellent! Your sales pipeline is ready to convert leads.',
            nextButton: 'Next: Customer Service tools',
            icon: Target
          },
          {
            id: 'sales-pipeline',
            title: 'Build your sales pipeline',
            description: 'Perfect! Continue with customer service to retain clients.',
            nextButton: 'Next: Customer Service tools',
            icon: Handshake
          }
        ];
      case 'customer-service':
        return [
          {
            id: 'service-basics',
            title: 'Recommended: Customer service setup complete',
            description: 'Great work! Your support system is ready to help customers.',
            nextButton: 'Next: Content tools',
            icon: Target
          },
          {
            id: 'support-system',
            title: 'Set up customer support workflows',
            description: 'Excellent! Continue with content tools for better engagement.',
            nextButton: 'Next: Content tools',
            icon: Headphones
          }
        ];
      case 'content':
        return [
          {
            id: 'content-basics',
            title: 'Recommended: Content strategy established',
            description: 'Fantastic! Your content foundation is solid.',
            nextButton: 'Next: Commerce tools',
            icon: Target
          },
          {
            id: 'content-creation',
            title: 'Create engaging content workflows',
            description: 'Well done! Ready to boost sales with commerce tools.',
            nextButton: 'Next: Commerce tools',
            icon: FileText
          }
        ];
      case 'commerce':
        return [
          {
            id: 'commerce-basics',
            title: 'Recommended: E-commerce foundation ready',
            description: 'Outstanding! Your online store is ready for customers.',
            nextButton: 'Complete Setup',
            icon: Target
          },
          {
            id: 'store-setup',
            title: 'Launch your online store',
            description: 'Perfect! Your e-commerce platform is fully operational.',
            nextButton: 'Complete Setup',
            icon: ShoppingCart
          }
        ];
      default:
        return [];
    }
  };

  const completedSections = getCompletedSections(activeGuide);

  // Handle Next button clicks to navigate to appropriate guide
  const handleNextButtonClick = (nextButtonText: string) => {
    console.log('🔄 Next button clicked:', nextButtonText);
    
    // Map button text to guide IDs
    if (nextButtonText.includes('Marketing tools')) {
      setActiveGuide('marketing');
    } else if (nextButtonText.includes('Sales tools')) {
      setActiveGuide('sales');
    } else if (nextButtonText.includes('Customer Service tools')) {
      setActiveGuide('customer-service');
    } else if (nextButtonText.includes('Content tools')) {
      setActiveGuide('content');
    } else if (nextButtonText.includes('Commerce tools')) {
      setActiveGuide('commerce');
    } else if (nextButtonText.includes('Complete Setup')) {
      // Show completion modal
      console.log('🎉 Setup completed!');
      setShowCompletionModal(true);
    }
  };

  // Reset completed sections when switching guides
  useEffect(() => {
    console.log('📚 Active guide changed to:', activeGuide, '- Closing completed sections');
    setShowCompletedSections(false);
  }, [activeGuide]);

  return (
    <div className="h-full bg-gray-50 overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - User Guides (Narrower & Shorter) */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200 h-fit">
            <div className="p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">User Guides</h2>
              
              {/* User Guide List - Very Compressed */}
              <div className="space-y-0">
                {userGuides.map((guide) => (
                  <button
                    key={guide.id}
                    onClick={() => {
                      console.log('📚 Guide selected:', guide.id);
                      setActiveGuide(guide.id);
                    }}
                    className={`w-full text-left py-1 px-2 text-sm transition-colors ${
                      guide.id === activeGuide
                        ? 'bg-teal-50 text-teal-800 border-l-[4px] border-teal-500'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {guide.title}
                  </button>
                ))}
              </div>

              {/* Plan Actions */}
              <div className="border-t border-gray-100 mt-3 pt-3">
                <div className="space-y-0.5">
                  {planActions.map((action) => {
                    const IconComponent = action.icon;
                    const TrailingIcon = action.trailingIcon;
                    return (
                      <button
                        key={action.id}
                        onClick={() => {
                          if (action.id === 'invite-team') {
                            setShowInviteModal(true);
                          }
                        }}
                        className="w-full flex items-center text-left py-0.5 px-1 text-sm text-gray-700 hover:text-gray-900 transition-colors"
                      >
                        <IconComponent className="h-3 w-3 text-gray-400 mr-2" />
                        <span className="flex-1">{action.title}</span>
                        <TrailingIcon className="h-3 w-3 text-gray-400 ml-2" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Wider (spans 2 columns) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Explore Banner - Only above right column */}
            {showBanner && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 relative">
                <button
                  onClick={() => setShowBanner(false)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="pr-8 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">
                    Explore the other features in your template
                  </h3>
                  <button 
                    onClick={() => setShowSetupSidebar(true)}
                    className="text-sm text-teal-600 hover:text-teal-700 underline"
                  >
                    See what's been set up
                  </button>
                </div>
              </div>
            )}

            {/* Progress Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-5">
                <currentGuide.icon className={`h-5 w-5 ${currentGuide.iconColor}`} />
                <h3 className="font-medium text-gray-900">Your {currentGuide.toolsName} tools progress</h3>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">Progress</span>
                  <span className="text-sm font-semibold text-gray-900">{Math.round(progressPercentage)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-teal-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Learn More Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Learn more about {currentGuide.toolsName.toLowerCase()} tools</h3>
              <div className="divide-y divide-gray-100">
                {learningLinks.map((link, index) => {
                  const IconComponent = link.icon;
                  return (
                    <button
                      key={link.id}
                      onClick={() => {
                        if (link.action === 'service-modal') {
                          setShowServiceModal(true);
                          setServiceModalPage(1);
                        } else if (link.url) {
                          window.open(link.url, '_blank');
                        }
                      }}
                      className={`w-full flex items-center text-left hover:bg-gray-50 transition-colors group pl-4 ${
                        index === 0 ? 'pb-4' : index === learningLinks.length - 1 ? 'pt-4' : 'py-4'
                      }`}
                    >
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gray-200 transition-colors mr-4">
                        <IconComponent className="h-5 w-5 text-gray-600" />
                      </div>
                      <span className="text-sm font-medium text-teal-600 group-hover:text-teal-700 transition-colors flex-1">
                        {link.title}
                      </span>
                      {link.external && (
                        <ExternalLink className="h-3 w-3 text-gray-400 ml-2" />
                      )}
                    </button>
                  );
                })}
              </div>

            </div>

            {/* Show Completed Sections - separate pill button below the card */}
            <div className="text-center">
              <button
                onClick={() => {
                  console.log('📋 Show completed sections clicked for:', activeGuide);
                  setShowCompletedSections(!showCompletedSections);
                }}
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-gray-300 rounded-full bg-white text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                <ChevronDown className={`h-4 w-4 mr-2 transition-transform ${showCompletedSections ? 'rotate-180' : ''}`} />
                {showCompletedSections ? 'Hide' : 'Show'} completed sections
              </button>
            </div>
            
            {/* Completed Sections Display */}
            {showCompletedSections && (
              <div className="mt-6 space-y-6">
                {completedSections.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <div key={section.id} className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-start space-x-3 mb-4">
                        <IconComponent className="h-5 w-5 text-orange-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-2">{section.title}</h4>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="h-8 w-8 text-teal-600" />
                        </div>
                        <p className="text-sm text-gray-600 mb-4">{section.description}</p>
                        <button 
                          onClick={() => handleNextButtonClick(section.nextButton)}
                          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                        >
                          {section.nextButton}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Finished Setting Up - Plain Text at Bottom */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Finished setting up?{' '}
              Turn off the user guide, and we won't show you this page again.
          </p>
        </div>
      </div>

      {/* Setup Sidebar */}
      {showSetupSidebar && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowSetupSidebar(false)}
          />
          
          {/* Sidebar */}
          <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Explore your other features</h2>
                <button
                  onClick={() => setShowSetupSidebar(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Subtitle */}
              <p className="text-gray-600 mb-8">
                This template is designed to help businesses like yours{' '}
                <span className="font-semibold">generate leads</span>.
              </p>

              {/* Contact Properties Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Contact properties</h3>
                </div>
                
                <p className="text-gray-600 mb-4 ml-9">
                  A set of properties designed to show you the contact information you need, at a glance.
                </p>
                
                <div className="ml-9">
                  <button 
                    onClick={() => {
                      console.log('Go to contacts button clicked');
                      setShowSetupSidebar(false);
                      console.log('Navigating to /contacts');
                      navigate('/contacts');
                    }}
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                  >
                    Go to contacts
                  </button>
                </div>
              </div>

              {/* Footer Message */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-gray-600 text-sm">
                  To finish setting up and start getting the most out of your CRM, visit the User Guide.
                </p>
              </div>

              {/* Finish Setup Button */}
              <button
                onClick={() => setShowSetupSidebar(false)}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Finish your setup
              </button>
            </div>
          </div>
        </>
      )}

      {/* Invite Team Modal */}
      {showInviteModal && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setShowInviteModal(false)}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative">
              {/* Close button */}
              <button
                onClick={() => setShowInviteModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>

              {/* Modal content */}
              <div className="p-6">
                {/* Icon and illustration area */}
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    {/* Envelope illustration */}
                    <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-green-100 rounded-2xl flex items-center justify-center relative overflow-hidden">
                      {/* Envelope base */}
                      <div className="w-16 h-12 bg-white rounded-lg shadow-lg relative">
                        {/* Envelope flap */}
                        <div className="absolute -top-2 left-0 right-0 h-6 bg-white transform rotate-12 rounded-t-lg shadow-sm"></div>
                        {/* HubSpot logo placeholder */}
                        <div className="absolute top-2 left-2 w-3 h-3 bg-orange-500 rounded-full"></div>
                        <div className="absolute top-2 left-6 w-6 h-1 bg-orange-500 rounded"></div>
                        <div className="absolute top-4 left-2 w-8 h-1 bg-orange-500 rounded"></div>
                      </div>
                      {/* Sparkle effects */}
                      <div className="absolute top-2 right-2 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                      <div className="absolute bottom-2 left-2 w-1 h-1 bg-yellow-400 rounded-full animate-pulse delay-100"></div>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-semibold text-gray-900 text-center mb-2">
                  Invite your team
                </h2>
                
                <p className="text-gray-600 text-center mb-6">
                  HubSpot is designed for collaboration — share data, assign contacts, and much more.
                </p>

                {/* Email input form */}
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  const email = formData.get('email') as string;
                  const makeAdmin = formData.get('makeAdmin') === 'on';
                  
                  // Here you would typically send the invitation
                  console.log('Inviting:', { email, makeAdmin });
                  
                  // Close modal after submission
                  setShowInviteModal(false);
                }}>
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="Enter email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>

                  {/* Make super admin checkbox */}
                  <div className="mb-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="makeAdmin"
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700 flex items-center">
                        Make super admin
                        <Info className="h-4 w-4 text-gray-400 ml-1" />
                      </span>
                    </label>
                  </div>

                  {/* Additional info */}
                  <div className="mb-6 text-sm text-gray-600">
                    <p>
                      Need more control over how your team uses HubSpot?{' '}
                      <button
                        type="button"
                        onClick={() => { console.log('Set custom access permissions clicked'); }}
                        className="text-teal-600 hover:text-teal-700 underline"
                      >
                        Set custom access permissions
                      </button>
                      .
                    </p>
                  </div>

                  {/* Submit button */}
                  <button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium transition-colors"
                  >
                    Send invite
                  </button>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Service Hub Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 relative">
            {/* Close button */}
            <button
              onClick={() => setShowServiceModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Navigation arrows - positioned outside the modal */}
            <button
              onClick={() => setServiceModalPage(Math.max(1, serviceModalPage - 1))}
              disabled={serviceModalPage === 1}
              className="absolute -left-16 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-white shadow-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed z-10 border border-gray-200"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>
            
            <button
              onClick={() => setServiceModalPage(Math.min(6, serviceModalPage + 1))}
              disabled={serviceModalPage === 6}
              className="absolute -right-16 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-white shadow-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed z-10 border border-gray-200"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>

            <div className="p-8 text-center">
              {/* Page 1 - Service Hub Free Overview */}
              {serviceModalPage === 1 && (
                <div className="space-y-6">
                  {/* Illustration */}
                  <div className="flex justify-center mb-6">
                    <div className="w-48 h-32 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg flex items-center justify-center">
                      <div className="relative">
                        {/* Desk/Table */}
                        <div className="w-20 h-12 bg-teal-300 rounded-lg"></div>
                        {/* Headphones */}
                        <div className="absolute -top-2 left-2 w-6 h-6 bg-gray-600 rounded-full opacity-80"></div>
                        <div className="absolute -top-1 left-3 w-4 h-4 bg-gray-500 rounded-full"></div>
                        {/* Monitor/Screen */}
                        <div className="absolute -top-4 right-2 w-8 h-6 bg-pink-200 rounded border-2 border-pink-300"></div>
                        {/* Small items */}
                        <div className="absolute top-2 left-6 w-3 h-2 bg-orange-200 rounded"></div>
                      </div>
                    </div>
                  </div>

                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    What is Service Hub Free?
                  </h2>
                  
                  <div className="max-w-lg mx-auto text-gray-600 leading-relaxed">
                    <p>
                      Service Hub Free is your all-in-one suite of customer support tools, designed to help 
                      you seamlessly manage intake and triage, so you can troubleshoot and solve any 
                      issues that arise. Combine that with your sales and marketing tools, and you can 
                      deepen your customer relationships at work, or on the go.
                    </p>
                  </div>

                  {/* Page indicator */}
                  <div className="text-sm text-gray-500 mt-8">
                    {serviceModalPage}/6
                  </div>

                  {/* Action button */}
                  <div className="mt-8">
                    <button 
                      onClick={() => {
                        setShowServiceModal(false);
                        setShowInviteModal(true);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      Connect your team email
                    </button>
                  </div>
                </div>
              )}

              {/* Page 2 - Intake: Receive customer support requests */}
              {serviceModalPage === 2 && (
                <div className="space-y-6">
                  {/* Illustration */}
                  <div className="flex justify-center mb-6">
                    <div className="w-48 h-32 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg flex items-center justify-center">
                      <div className="relative">
                        {/* Inbox/Container */}
                        <div className="w-24 h-16 bg-orange-200 rounded-lg border-2 border-orange-300"></div>
                        {/* Email envelopes */}
                        <div className="absolute -top-2 left-2 w-8 h-6 bg-white rounded border border-gray-300 transform rotate-12">
                          <div className="w-2 h-1 bg-orange-400 rounded-full absolute top-1 left-1"></div>
                        </div>
                        <div className="absolute -top-1 right-1 w-8 h-6 bg-white rounded border border-gray-300 transform -rotate-6">
                          <div className="w-full h-0.5 bg-gray-300 absolute top-2"></div>
                          <div className="w-2/3 h-0.5 bg-gray-300 absolute top-3"></div>
                        </div>
                        <div className="absolute top-2 left-4 w-7 h-5 bg-white rounded border border-gray-300 transform rotate-3">
                          <div className="w-full h-0.5 bg-gray-300 absolute top-1"></div>
                          <div className="w-3/4 h-0.5 bg-gray-300 absolute top-2"></div>
                        </div>
                        {/* Phone/support icon */}
                        <div className="absolute -right-2 top-1 w-6 h-6 bg-teal-200 rounded-full flex items-center justify-center">
                          <div className="w-3 h-3 bg-teal-600 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    Intake: Receive customer support requests
                  </h2>
                  
                  <div className="max-w-lg mx-auto text-gray-600 leading-relaxed">
                    <p>
                      Support requests can come through a wide range of channels. With live chat, a shared 
                      support inbox for your team, call logging, forms and more, we'll help you track and 
                      manage them all in one place.
                    </p>
                  </div>

                  {/* Page indicator */}
                  <div className="text-sm text-gray-500 mt-8">
                    {serviceModalPage}/6
                  </div>

                  {/* Action button */}
                  <div className="mt-8">
                    <button 
                      onClick={() => {
                        setShowServiceModal(false);
                        setShowInviteModal(true);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      Connect your team email
                    </button>
                  </div>
                </div>
              )}

              {/* Page 3 - Triage: Group requests, and assign the right people */}
              {serviceModalPage === 3 && (
                <div className="space-y-6">
                  {/* Illustration */}
                  <div className="flex justify-center mb-6">
                    <div className="w-48 h-32 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                      <div className="relative">
                        {/* Main container/box */}
                        <div className="w-20 h-14 bg-white rounded-lg border-2 border-blue-300 shadow-sm"></div>
                        {/* Layered documents/tickets */}
                        <div className="absolute -top-1 left-1 w-16 h-10 bg-yellow-100 rounded border border-yellow-300"></div>
                        <div className="absolute top-0 left-2 w-16 h-10 bg-blue-100 rounded border border-blue-300"></div>
                        <div className="absolute top-1 left-3 w-16 h-10 bg-pink-100 rounded border border-pink-300"></div>
                        {/* Checkmark badge */}
                        <div className="absolute -top-3 -right-2 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                          <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                            <div className="w-2 h-1 bg-teal-500 rounded-full"></div>
                          </div>
                        </div>
                        {/* Dotted circle background */}
                        <div className="absolute -inset-4 border-2 border-dashed border-gray-300 rounded-full opacity-50"></div>
                      </div>
                    </div>
                  </div>

                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    Triage: Group requests, and assign the right people
                  </h2>
                  
                  <div className="max-w-lg mx-auto text-gray-600 leading-relaxed">
                    <p>
                      Use tickets and filters to categorize your requests, prioritize them for quick responses, 
                      and assign them to a member of your team so they can offer the right help at the right 
                      time.
                    </p>
                  </div>

                  {/* Page indicator */}
                  <div className="text-sm text-gray-500 mt-8">
                    {serviceModalPage}/6
                  </div>

                  {/* Action button */}
                  <div className="mt-8">
                    <button 
                      onClick={() => {
                        setShowServiceModal(false);
                        setShowInviteModal(true);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      Connect your team email
                    </button>
                  </div>
                </div>
              )}

              {/* Page 4 - Troubleshoot: Understand the issue */}
              {serviceModalPage === 4 && (
                <div className="space-y-6">
                  {/* Illustration */}
                  <div className="flex justify-center mb-6">
                    <div className="w-48 h-32 bg-gradient-to-br from-orange-50 to-pink-50 rounded-lg flex items-center justify-center">
                      <div className="relative">
                        {/* Chat bubbles */}
                        <div className="w-16 h-10 bg-orange-200 rounded-2xl border border-orange-300">
                          <div className="w-full h-1 bg-orange-400 rounded-full mt-2"></div>
                          <div className="w-3/4 h-1 bg-orange-400 rounded-full mt-1 ml-1"></div>
                          <div className="w-1/2 h-1 bg-orange-400 rounded-full mt-1 ml-2"></div>
                        </div>
                        <div className="absolute -top-2 left-8 w-14 h-9 bg-blue-200 rounded-2xl border border-blue-300">
                          <div className="w-full h-1 bg-blue-400 rounded-full mt-2"></div>
                          <div className="w-2/3 h-1 bg-blue-400 rounded-full mt-1 ml-1"></div>
                        </div>
                        <div className="absolute top-2 left-12 w-12 h-8 bg-yellow-200 rounded-2xl border border-yellow-300">
                          <div className="w-full h-1 bg-yellow-500 rounded-full mt-1.5"></div>
                          <div className="w-3/4 h-1 bg-yellow-500 rounded-full mt-1 ml-0.5"></div>
                        </div>
                        {/* Sparkle effects */}
                        <div className="absolute -top-1 -left-2 w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <div className="absolute top-0 right-2 w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <div className="absolute bottom-1 -left-1 w-1 h-1 bg-yellow-400 rounded-full"></div>
                      </div>
                    </div>
                  </div>

                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    Troubleshoot: Understand the issue
                  </h2>
                  
                  <div className="max-w-lg mx-auto text-gray-600 leading-relaxed">
                    <p>
                      With your Service Hub Free tools, you have everything you need to understand the 
                      issue, find the relevant context, and diagnose the problem. And, most importantly: find 
                      a solution with an actionable outcome.
                    </p>
                  </div>

                  {/* Page indicator */}
                  <div className="text-sm text-gray-500 mt-8">
                    {serviceModalPage}/6
                  </div>

                  {/* Action button */}
                  <div className="mt-8">
                    <button 
                      onClick={() => {
                        setShowServiceModal(false);
                        setShowInviteModal(true);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      Connect your team email
                    </button>
                  </div>
                </div>
              )}

              {/* Page 5 - Solve: Delight your customers */}
              {serviceModalPage === 5 && (
                <div className="space-y-6">
                  {/* Illustration */}
                  <div className="flex justify-center mb-6">
                    <div className="w-48 h-32 bg-gradient-to-br from-green-50 to-teal-50 rounded-lg flex items-center justify-center">
                      <div className="relative">
                        {/* Central lightbulb/solution icon */}
                        <div className="w-12 h-16 bg-gradient-to-b from-yellow-200 to-yellow-300 rounded-t-full border-2 border-yellow-400">
                          <div className="w-8 h-8 bg-yellow-100 rounded-full mx-auto mt-2 border border-yellow-300"></div>
                        </div>
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-3 bg-gray-300 rounded-b"></div>
                        
                        {/* Scattered celebration elements */}
                        <div className="absolute -top-2 -left-3 w-3 h-3 bg-blue-400 rounded-full"></div>
                        <div className="absolute -top-1 right-2 w-2 h-2 bg-pink-400 rounded-full"></div>
                        <div className="absolute top-2 -right-4 w-2.5 h-2.5 bg-purple-400 rounded-full"></div>
                        <div className="absolute top-6 -left-4 w-2 h-2 bg-green-400 rounded-full"></div>
                        <div className="absolute bottom-2 -right-3 w-3 h-3 bg-orange-400 rounded-full"></div>
                        <div className="absolute bottom-1 -left-2 w-2 h-2 bg-red-400 rounded-full"></div>
                        
                        {/* Star */}
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-yellow-400" style={{clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'}}>
                        </div>
                        
                        {/* Diamond shapes */}
                        <div className="absolute top-1 -left-5 w-3 h-3 bg-teal-400 transform rotate-45"></div>
                        <div className="absolute bottom-3 right-4 w-2 h-2 bg-blue-500 transform rotate-45"></div>
                        
                        {/* Background glow */}
                        <div className="absolute -inset-6 bg-gradient-to-r from-green-100 to-teal-100 rounded-full opacity-30"></div>
                      </div>
                    </div>
                  </div>

                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    Solve: Delight your customers
                  </h2>
                  
                  <div className="max-w-lg mx-auto text-gray-600 leading-relaxed">
                    <p>
                      This is the moment your customers are waiting for. With everything logged in their 
                      contact record, you can solve their issues with full context, and answer any questions 
                      they might have.
                    </p>
                  </div>

                  {/* Page indicator */}
                  <div className="text-sm text-gray-500 mt-8">
                    {serviceModalPage}/6
                  </div>

                  {/* Action button */}
                  <div className="mt-8">
                    <button 
                      onClick={() => {
                        setShowServiceModal(false);
                        setShowInviteModal(true);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      Connect your team email
                    </button>
                  </div>
                </div>
              )}

              {/* Page 6 - Improve: Draw insights from every request */}
              {serviceModalPage === 6 && (
                <div className="space-y-6">
                  {/* Illustration */}
                  <div className="flex justify-center mb-6">
                    <div className="w-48 h-32 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg flex items-center justify-center">
                      <div className="relative">
                        {/* Chart/Graph */}
                        <div className="w-16 h-12 bg-white rounded border border-gray-300 p-1">
                          <div className="w-full h-full bg-gradient-to-t from-purple-200 to-purple-100 rounded-sm">
                            {/* Line graph */}
                            <div className="w-full h-0.5 bg-purple-400 absolute top-6 left-1"></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full absolute top-5 left-2"></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full absolute top-4 left-5"></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full absolute top-3 left-8"></div>
                          </div>
                        </div>
                        
                        {/* Bar charts */}
                        <div className="absolute top-0 right-2 flex space-x-1 items-end">
                          <div className="w-3 h-8 bg-teal-300 rounded-t"></div>
                          <div className="w-3 h-10 bg-teal-400 rounded-t"></div>
                          <div className="w-3 h-6 bg-teal-200 rounded-t"></div>
                        </div>
                        
                        {/* Pie chart */}
                        <div className="absolute bottom-0 left-0 w-10 h-10 rounded-full overflow-hidden">
                          <div className="w-full h-full bg-gradient-to-br from-pink-300 via-blue-300 to-purple-300"></div>
                          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-pink-400" style={{clipPath: 'polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%)'}}>
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-blue-400" style={{clipPath: 'polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%)'}}>
                          </div>
                        </div>
                        
                        {/* Background glow */}
                        <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full opacity-40"></div>
                      </div>
                    </div>
                  </div>

                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    Improve: Draw insights from every request
                  </h2>
                  
                  <div className="max-w-lg mx-auto text-gray-600 leading-relaxed">
                    <p>
                      With powerful report dashboards, you can take each enquiry as an opportunity. Use 
                      your new insights to reduce your response times, manage the volume of enquiries, and 
                      improve your service.
                    </p>
                  </div>

                  {/* Page indicator */}
                  <div className="text-sm text-gray-500 mt-8">
                    {serviceModalPage}/6
                  </div>

                  {/* Action button */}
                  <div className="mt-8">
                    <button 
                      onClick={() => {
                        setShowServiceModal(false);
                        setShowInviteModal(true);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      Connect your team email
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Completion Modal */}
      {showCompletionModal && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setShowCompletionModal(false)}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative">
              {/* Close button */}
              <button
                onClick={() => setShowCompletionModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>

              {/* Modal content */}
              <div className="p-6 text-center">
                {/* Success icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                
                {/* Title */}
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  🎉 Congratulations!
                </h2>
                
                {/* Description */}
                <p className="text-gray-600 mb-6">
                  You've successfully completed your CRM setup! Your system is now ready to help you manage customers, track sales, and grow your business.
                </p>
                
                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button 
                    onClick={() => {
                      setShowCompletionModal(false);
                      navigate('/leads');
                    }}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                  >
                    Start Managing Leads
                  </button>
                  <button 
                    onClick={() => setShowCompletionModal(false)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm font-medium transition-colors"
                  >
                    Stay Here
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HomePage;
